import React, { type ReactElement } from 'react';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import type { FilterOrControlId } from '../../../constants/filters/types';

import { getIcon } from './utils';

const baseStyles = xcss({
	height: '32px',
	paddingInline: 'space.150',
	width: '100%',
	borderRadius: 'radius.small',
	color: 'color.text',
	backgroundColor: 'color.background.neutral',

	':hover': {
		backgroundColor: 'color.background.neutral.hovered',
	},
});

const selectedStyles = xcss({
	backgroundColor: 'color.background.selected',
	color: 'color.text.accent.blue',

	':hover': {
		backgroundColor: 'color.background.selected.hovered',
	},
});

type ButtonIconProps = {
	filterOrControlId: FilterOrControlId;
	isSelected: boolean;
};

const buttonIconStyles = xcss({
	display: 'flex',
});

const ButtonIconOld = ({ filterOrControlId, isSelected }: ButtonIconProps) => {
	const buttonIcon = getIcon({ filterOrControlId, isSelected });
	return <Box xcss={buttonIconStyles}>{buttonIcon}</Box>;
};

const buttonWrapperStyles = xcss({
	maxWidth: '280px', // matches dropdown of 312px (and padding)
});

const textStyles = xcss({
	// ellipsis for overflow text
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
});

export type FilterButtonOldProps = {
	otherProps: { [key: string]: any };
	isSelected: boolean;
	/**
	 * If a string and there are selected elements, some
	 * information about the selected items will be shown.
	 * Otherwise, the label will be shown as provided.
	 */
	label?: ReactElement | string;
	filterOrControlId?: FilterOrControlId;
	selectionBadge?: ReactElement | null;
	onClick?: () => void;
	onKeyDown?: (event: React.KeyboardEvent) => void;
	wide?: boolean;
	testId?: string;
	ariaLabel?: string;
	iconBefore?: ReactElement | null;
};

// TODO: Remove this component when cleaning up useFiltersDiscoverability2Enabled. The whole "filter-button-old" folder should be removed
export const FilterButtonOld = ({
	otherProps,
	isSelected,
	filterOrControlId,
	label,
	selectionBadge,
	onClick,
	onKeyDown,
	wide,
	testId,
	ariaLabel,
	iconBefore,
}: FilterButtonOldProps): React.JSX.Element => (
	<Box xcss={!wide && buttonWrapperStyles}>
		<Pressable
			onClick={onClick}
			onKeyDown={onKeyDown}
			{...otherProps}
			{...(ariaLabel && { ['aria-label']: ariaLabel })}
			testId={testId}
			xcss={[baseStyles, isSelected && selectedStyles]}
		>
			<Inline alignInline="start" space="space.100" spread="space-between" alignBlock="center">
				<Inline alignBlock="center" alignInline="start" xcss={textStyles} space="space.100">
					<Inline space="space.075">
						{iconBefore}
						{filterOrControlId && (
							<ButtonIconOld filterOrControlId={filterOrControlId} isSelected={isSelected} />
						)}
					</Inline>
					{label && <Box xcss={textStyles}>{label}</Box>}
					{selectionBadge}
				</Inline>
				{filterOrControlId !== 'dropdown' && (
					<ChevronDownIcon
						label=""
						color={isSelected ? token('color.icon.selected') : token('color.icon')}
						size="small"
					/>
				)}
			</Inline>
		</Pressable>
	</Box>
);
